import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Radio, KeyRound } from 'lucide-react';
import { useAuthStore } from '@/state/authStore';
import { formatCodeInput } from './betaLoginUtils';

/**
 * Phase 16.3.1 — Login-Page für Closed-Beta-Tester.
 *
 * Zeigt sich, wenn `/play` ohne gültige Session aufgerufen wird (siehe
 * AuthGuard). Format des Codes ist BETA-XXXX-YYYY — das Eingabefeld
 * formatiert während der Eingabe automatisch in Großbuchstaben + Bindestriche.
 */
export default function BetaLogin() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await login(code.trim());
      if (result.ok) {
        navigate('/play', { replace: true });
      } else {
        setError(result.error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-primary px-4">
      <div className="w-full max-w-md rounded-xl border border-amber-500/40 bg-bg-secondary shadow-2xl">
        <header className="flex items-center gap-3 border-b border-amber-500/40 bg-amber-500/10 px-6 py-4">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-emergency">
            <Radio className="h-5 w-5 text-white" aria-hidden />
          </span>
          <div>
            <p className="font-display text-lg font-bold">
              <span aria-hidden>🧪</span> Closed Beta · Zugang
            </p>
            <p className="text-xs text-fg-muted">
              Dispatch<span className="text-accent-emergency">24</span> v
              {typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev'}
            </p>
          </div>
        </header>

        <form onSubmit={onSubmit} className="space-y-4 px-6 py-6">
          <div>
            <label htmlFor="beta-code" className="mb-2 block text-sm font-semibold">
              Gib deinen persönlichen Beta-Code ein
            </label>
            <div className="relative">
              <KeyRound
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted"
                aria-hidden
              />
              <input
                id="beta-code"
                type="text"
                value={code}
                onChange={(e) => setCode(formatCodeInput(e.target.value))}
                placeholder="BETA-XXXX-YYYY"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                disabled={submitting}
                data-testid="beta-code-input"
                className="w-full rounded-md border border-border-subtle bg-bg-primary py-3 pl-10 pr-3 font-mono text-base tracking-wider text-fg-primary outline-none transition focus:border-accent-dispatch disabled:opacity-50"
              />
            </div>
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-md border border-accent-emergency/40 bg-accent-emergency/10 p-3 text-sm text-accent-emergency"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || code.trim().length === 0}
            data-testid="beta-login-submit"
            className="w-full rounded-md bg-accent-emergency px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-accent-emergency-hover disabled:cursor-not-allowed disabled:bg-bg-elevated disabled:text-fg-muted"
          >
            {submitting ? 'Prüfe …' : 'Einloggen'}
          </button>

          <p className="pt-2 text-center text-xs text-fg-muted">
            Kein Code? Wende dich an den Entwickler.
          </p>
        </form>
      </div>
    </main>
  );
}

